/**
 * QuickshopBundlePicker — swaps between sibling bundle products inside the quick shop modal
 * without navigating.
 *
 * Same baseline as `bundle-picker.js`: the swatches are ordinary anchors
 * (`snippets/quickshop-bundle-group.liquid`), so the selector is complete and crawlable on its
 * own, and this component only upgrades the click. Unlike `bundle-picker.js`, which fetches the
 * sibling's whole page and morphs `<main>`, this fetches the sibling's page and pulls out its
 * hidden `[data-quickshop-fragment]` (rendered by `pdp-info.liquid`, same markup as this modal's
 * own body — see `quickshop-fragment.liquid`'s doc) and morphs that into the open
 * `<quickshop-product-form>`. No history entry is pushed: quick shop is an overlay on a listing
 * page, not the PDP, so there is no URL to keep in sync.
 *
 * After the morph, `quickshop-product-form.js`'s cached variant state is stale (its children,
 * including the variants script, were just replaced without `connectedCallback` re-running), so
 * `refresh()` is called on it explicitly.
 *
 * Any failure falls back to a real navigation, same as `bundle-picker.js`.
 *
 * DOM contract:
 *   <quickshop-bundle-picker>          — wraps both axis groups, lives inside a
 *                                        <quickshop-product-form>
 *   a[data-quickshop-bundle-option]    — one swatch; its href is the sibling bundle's product URL
 *   [aria-current="true"]              — the swatch already being shown; clicking it is a no-op
 */

import { morph, MORPH_OPTIONS } from "@theme/morph";

const OPTION_SELECTOR = "a[data-quickshop-bundle-option]";

class QuickshopBundlePicker extends HTMLElement {
  /** @type {AbortController | null} */
  #controller = null;

  connectedCallback() {
    this.addEventListener("click", this.#handleClick);
  }

  disconnectedCallback() {
    this.removeEventListener("click", this.#handleClick);
    this.#controller?.abort();
  }

  /** @param {MouseEvent} event */
  #handleClick = (event) => {
    if (event.defaultPrevented || event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    const target = event.target;
    const link = target instanceof Element ? target.closest(OPTION_SELECTOR) : null;
    if (!link || !this.contains(link)) return;

    event.preventDefault();
    if (link.getAttribute("aria-current") === "true") return;

    this.#swap(/** @type {HTMLAnchorElement} */ (link).href);
  };

  /** @param {string} url - The sibling bundle product's URL */
  async #swap(url) {
    const form = this.closest("quickshop-product-form");
    if (!form) {
      window.location.assign(url);
      return;
    }

    this.#controller?.abort();
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    form.setAttribute("aria-busy", "true");

    try {
      const response = await fetch(url, { signal });
      if (!response.ok) throw new Error(`Quick shop bundle swap failed with ${response.status}`);

      const document_ = new DOMParser().parseFromString(await response.text(), "text/html");
      const fragment = document_.querySelector("[data-quickshop-fragment]");
      if (!fragment) throw new Error("Quick shop bundle swap response has no [data-quickshop-fragment]");

      morph(form, fragment, MORPH_OPTIONS);

      if (typeof (/** @type {any} */ (form).refresh) === "function") {
        /** @type {any} */ (form).refresh();
      }

      const body = form.querySelector(".quickshop-fragment__body");
      body?.scrollTo({ top: 0, behavior: "instant" });
    } catch (error) {
      if (error instanceof Error && error.name === "AbortError") return;
      console.error(error);
      window.location.assign(url);
    } finally {
      form.removeAttribute("aria-busy");
    }
  }
}

if (!customElements.get("quickshop-bundle-picker")) {
  customElements.define("quickshop-bundle-picker", QuickshopBundlePicker);
}

export default QuickshopBundlePicker;
